import {Anchor, Dialogue, Heading, Text} from '@albert-io/atomic';
import React from 'react';
import {useTracking} from 'react-tracking';

import withTracking from 'lib/hocs/withTracking';

import './get-started-with-albert.scss';

const TrackedLink = withTracking(Anchor, {
  onClick: {
    event: 'click',
    type: 'hyperlink',
    content: (e, props) => props.children
  }
});

const teacherTalkUrl = 'https://app.lemcal.com/@albert-adoption-team/teacher-talk';

interface HelpSectionProps {
  title: string;
  items: {label: string; href: string}[];
}

const helpItems = [
  {
    title: 'Getting started',
    items: [
      {
        label: 'Set up your classrooms',
        href: 'https://help.albert.io/en/articles/1884980-teacher-onboarding-getting-started'
      }
    ]
  },
  {
    title: 'Finding content',
    items: [
      {
        label: 'Discover questions to assign',
        href: 'https://help.albert.io/en/articles/1885026-how-do-i-search-for-questions-on-albert'
      }
    ]
  },
  {
    title: 'Assigning work',
    items: [
      {
        label: 'Assign students practice',
        href: 'https://help.albert.io/en/articles/4942861-how-can-i-assign-practice-on-albert'
      }
    ]
  },
  {
    title: 'Results & data',
    items: [
      {
        label: 'View class and student data',
        href: 'https://help.albert.io/en/articles/6683815-how-do-i-use-reports-for-data-analysis-as-a-teacher'
      }
    ]
  }
] as HelpSectionProps[];

const HelpSection = ({title, items}: HelpSectionProps) => (
  <div className='mt-x3'>
    <Heading size='2xs' className='mb-x1 block uppercase'>
      {title}
    </Heading>
    {items.map((item) => (
      <TrackedLink
        key={item.href}
        as='a'
        href={item.href}
        target='_blank'
        rel='noopener noreferrer'
      >
        {item.label}
      </TrackedLink>
    ))}
  </div>
);

const GetStartedWithAlbert = () => {
  const {Track} = useTracking({
    component: 'get started with albert'
  });

  return (
    <Track>
      <Dialogue
        className='get-started-with-albert w-inherit mt-x4 mx-auto max-w-[448px]'
        paddingLevel={4}
        hideCloseBtn
      >
        <Heading size='s'>Getting started with Albert</Heading>
        <Dialogue.Body className='mt-x3'>
          <div className='teacher-classes__class-cards-get-started-container__get-started-links mt-x3'>
            {helpItems.map((item) => (
              <HelpSection key={item.title} {...item} />
            ))}
          </div>

          <div className='mt-x6'>
            <Text size='s'>
              Not finding what you need? Search our{' '}
              <TrackedLink
                as='a'
                href='https://help.albert.io/en/collections/521216-teachers'
                target='_blank'
                rel='noopener noreferrer'
              >
                Help Center
              </TrackedLink>{' '}
              to learn more.
            </Text>
            <Text size='s' as='div' className='mt-x1'>
              Still need help?{' '}
              <TrackedLink as='a' href={teacherTalkUrl} target='_blank' rel='noopener noreferrer'>
                Book a free 15-minute personalized training session.
              </TrackedLink>
            </Text>
          </div>
        </Dialogue.Body>
      </Dialogue>
    </Track>
  );
};

export default GetStartedWithAlbert;
